import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Div, Icon, Text } from '@pinpoint/design-kit';

import BaseDialog from './base';
/* eslint-disable react/prop-types */

const visibleModelVariationOptions = [
  { label: 'Unremediated', value: 'unremediated' },
  { label: 'Remediated', value: 'remediated' },
];

export function ApprovalDialog({ isOpen, actions, isHidden = false }) {
  const [visibleModelVariation, setVisibleModelVariation] = useState('unremediated');

  useEffect(() => {
    if (isOpen) setVisibleModelVariation('unremediated');
  }, [isOpen]);

  const dialogActions = {
    ...actions,
    handlers: (actions.handlers ?? []).map((handler, idx) =>
      idx === 0
        ? {
            ...handler,
            on: () => handler.on(visibleModelVariation),
          }
        : handler
    ),
  };
  const content = [
    <Text key="approval-confirmation" textSize="paragraph" textWeight="500" textColor="shade66">
      Are you sure you want to approve the model?
    </Text>,
    <Div key="visible-model-variation" d="flex" align="center" justify="space-between" m={{ t: '1.5rem' }}>
      <Div d="flex" flexDir="column" p={{ r: '1rem' }}>
        <Text textSize="input" textWeight="500" textColor={isHidden ? 'shade99' : 'shade66'}>
          Model shown to clients
        </Text>
        <Text textSize="caption" textColor="shade99" m={{ t: '0.125rem' }}>
          {isHidden
            ? 'This model is hidden from clients. Change its visibility before selecting which variation clients see.'
            : 'Choose the result clients see after remediation.'}
        </Text>
      </Div>
      <Div
        d="flex"
        border="1px solid"
        borderColor={isHidden ? 'shadeCC' : 'shade66'}
        rounded="md"
        overflow="hidden"
        data-test="visible-model-variation-select"
        role="radiogroup"
        aria-label="Model shown to clients"
        aria-disabled={isHidden}
      >
        {visibleModelVariationOptions.map(({ label, value }, idx) => {
          const selected = visibleModelVariation === value;
          return (
            <React.Fragment key={value}>
              {idx > 0 && <Div w="1px" minW="1px" bg={isHidden ? 'shade99' : 'shade66'} />}
              <Button
                type="button"
                onClick={() => setVisibleModelVariation(value)}
                disabled={isHidden}
                bg={isHidden ? 'shadeF2' : selected ? 'brand500' : 'white'}
                hoverBg={isHidden ? 'shadeF2' : selected ? 'brand500' : 'shadeF2'}
                border="none"
                rounded="0"
                m="0"
                p={{ x: '0.75rem', y: '0.375rem' }}
                role="radio"
                aria-checked={selected}
                data-test={`visible-model-variation-${value}`}
              >
                <Text
                  textSize="caption"
                  textWeight="500"
                  textColor={isHidden ? 'shade99' : selected ? 'white' : 'shade66'}
                  whiteSpace="nowrap"
                >
                  {label}
                </Text>
              </Button>
            </React.Fragment>
          );
        })}
      </Div>
    </Div>,
  ];

  return (
    <BaseDialog
      isOpen={isOpen}
      actions={dialogActions}
      title={{
        text: `Approve Model`,
        backgroundColor: 'talLime',
        color: 'white',
        textSize: 'title',
        icon: <Icon name="AlertSolid" color="white" viewBox="0 0 24 24" size="3.125rem" m={{ r: '1rem' }} />,
      }}
      items={{
        content,
        buttons: [
          {
            text: 'YES',
            textSize: 'body',
            close: true,
          },
        ],
      }}
    />
  );
}

/* eslint-disable react/require-default-props */
ApprovalDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isHidden: PropTypes.bool,
  actions: PropTypes.shape({
    cancel: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    close: PropTypes.shape({
      before: PropTypes.func,
      on: PropTypes.func.isRequired,
      after: PropTypes.func,
    }).isRequired,
    handlers: PropTypes.arrayOf(
      PropTypes.shape({
        before: PropTypes.func,
        on: PropTypes.func.isRequired,
        after: PropTypes.func,
      })
    ),
  }).isRequired,
};
/* eslint-enable */

export default ApprovalDialog;
